import { useState, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { X, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { tourSteps } from "@/lib/helpContent";

interface OnboardingTourProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function OnboardingTour({ open, onClose, onComplete }: OnboardingTourProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);

  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  const step = tourSteps[stepIndex];

  useLayoutEffect(() => {
    if (!open || !step) return;

    let cancelled = false;
    const tryFindElement = (attempt = 0) => {
      if (cancelled) return;
      const el = document.querySelector(step.selector) as HTMLElement | null;
      if (!el) {
        // Försök igen några gånger ifall sidan fortfarande renderar
        if (attempt < 8) {
          setTimeout(() => tryFindElement(attempt + 1), 200);
        } else {
          setRect(null);
        }
        return;
      }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => {
        if (cancelled) return;
        const r = el.getBoundingClientRect();
        setRect({
          top: r.top,
          left: r.left,
          width: r.width,
          height: r.height,
        });
      }, 300);
    };

    const updateRect = () => tryFindElement(0);

    updateRect();
    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);
    return () => {
      cancelled = true;
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [open, step, stepIndex]);

  if (!open || !step) return null;

  const isLast = stepIndex === tourSteps.length - 1;
  const isFirst = stepIndex === 0;

  const handleNext = () => {
    if (isLast) {
      onComplete();
    } else {
      setStepIndex((i) => i + 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  // Tooltip-position: under highlighten om möjligt, annars över
  const tooltipPos = (() => {
    if (!rect) {
      return {
        top: window.innerHeight / 2 - 80,
        left: window.innerWidth / 2 - 160,
      };
    }
    const tooltipWidth = Math.min(420, window.innerWidth - 24);
    const tooltipHeight = 280;
    const margin = 12;

    let top = rect.top + rect.height + margin;
    if (top + tooltipHeight > window.innerHeight) {
      top = Math.max(margin, rect.top - tooltipHeight - margin);
    }

    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    left = Math.max(margin, Math.min(left, window.innerWidth - tooltipWidth - margin));

    return { top, left };
  })();

  return createPortal(
    <div className="fixed inset-0 z-[100]" role="dialog" aria-modal="true" aria-label={step.title}>
      {/* Mörk overlay med "hål" */}
      <svg className="absolute inset-0 w-full h-full pointer-events-auto" onClick={handleSkip}>
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {rect && (
              <rect
                x={rect.left - 6}
                y={rect.top - 6}
                width={rect.width + 12}
                height={rect.height + 12}
                rx="8"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.65)"
          mask="url(#tour-mask)"
        />
      </svg>

      {/* Highlight-ring */}
      {rect && (
        <div
          className="absolute pointer-events-none rounded-lg ring-2 ring-focus-ring ring-offset-2 ring-offset-focus-ring-offset animate-pulse"
          style={{
            top: rect.top - 6,
            left: rect.left - 6,
            width: rect.width + 12,
            height: rect.height + 12,
          }}
        />
      )}

      {/* Tooltip */}
      <div
        className="absolute bg-card border rounded-lg shadow-2xl p-5 w-[calc(100vw-24px)] sm:w-[420px] max-w-[420px] pointer-events-auto"
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="font-semibold text-sm">{step.title}</h3>
          <button
            onClick={handleSkip}
            className="text-muted-foreground hover:text-foreground shrink-0"
            aria-label="Stäng rundtur"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{step.body}</p>
        <div className="flex flex-col gap-3">
          <div className="flex w-full gap-2">
            {!isFirst && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setStepIndex((i) => i - 1)}
                className="flex-1 min-w-0"
              >
                <ChevronLeft className="h-4 w-4 mr-1 shrink-0" />
                Tillbaka
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={handleSkip}
              className="flex-1 min-w-0"
            >
              Hoppa över
            </Button>
            <Button
              size="sm"
              onClick={handleNext}
              className="flex-1 min-w-0"
            >
              {isLast ? "Klar" : "Nästa"}
              {!isLast && <ChevronRight className="h-4 w-4 ml-1 shrink-0" />}
            </Button>
          </div>
          <span className="text-xs text-muted-foreground text-center">
            Steg {stepIndex + 1} av {tourSteps.length}
          </span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
